/**
 * A tela das permissões de uma pessoa (ADR-0034, #1190), sem React e sem rede.
 *
 * ── O estado é o que a pessoa VAI poder ──────────────────────────────────
 *
 * A tela abre com as permissões efetivas dela (o padrão do papel com os
 * ajustes). Cada toggle mexe nesse estado, e nada muda até salvar. Salvar
 * manda o que difere do padrão do papel; a API normaliza de novo, e é ela que
 * decide.
 *
 * ── As dependências valem aqui também ────────────────────────────────────
 *
 * Uma ação só liga com o que ela exige ligado ("Ver as conversas" para
 * responder), e desligar um pré-requisito desliga o que depende dele, em
 * cadeia. A API aplica a mesma regra ao calcular o que a pessoa pode: a tela
 * só mostra antes de salvar o que vai valer.
 *
 * ── Os limites de quem só gerencia permissões ────────────────────────────
 *
 * Quem não é Administrador nem Super Admin só liga o que tem, e só sobe o
 * alcance até o dela (ADR-0034, item 5). A tela trava o que a API recusaria,
 * com a mesma conta: "ligar" é passar de desligado a ligado em relação ao que
 * está salvo, e o alcance só conta nos assuntos com "Até onde vale".
 */

import type {
  AcaoDePermissao,
  CatalogoDePermissoes,
  Me,
  PapelDaOrganizacao,
  PermissoesDaPessoa,
  RecursoDePermissao,
  SalvarPermissoesDaPessoa,
} from '@cliqcrm/contracts';

export type Alcance = 'propria' | 'time' | 'unidade' | 'tenant';

/** O que a pessoa vai poder, como a tela guarda enquanto se mexe. */
export interface EstadoDasPermissoes {
  /** `recurso:ação` ligadas. */
  readonly ligadas: ReadonlySet<string>;
  /**
   * recurso → até onde vale. Todo assunto tem um, mesmo sem ação ligada: o do
   * padrão do papel, que é o que passa a valer quando uma ação dele ligar.
   */
  readonly alcances: Readonly<Record<string, Alcance>>;
}

export const NIVEIS: readonly Alcance[] = ['propria', 'time', 'unidade', 'tenant'];

export const NOME_DO_ALCANCE: Readonly<Record<Alcance, string>> = {
  propria: 'Só o que é dela',
  time: 'O time',
  unidade: 'A unidade',
  tenant: 'A organização inteira',
};

const chave = (recurso: string, acao: string): string => `${recurso}:${acao}`;

/** A posição do alcance na escada; -1 para o que não é alcance (nenhum). */
const nivel = (alcance: string | undefined): number =>
  alcance === undefined ? -1 : NIVEIS.indexOf(alcance as Alcance);

type Lista = PermissoesDaPessoa['efetivas'];

/** O alcance do padrão do papel em cada assunto, mesmo onde o papel não tem ação. */
export function alcancesDoPapel(
  catalogo: CatalogoDePermissoes,
  papel: PapelDaOrganizacao,
): Record<string, Alcance> {
  const alcances: Record<string, Alcance> = {};
  const doPapel = catalogo.padroes.find((padrao) => padrao.papel === papel);
  for (const item of doPapel?.permissoes ?? []) alcances[item.recurso] = item.alcance;
  return alcances;
}

/**
 * O estado de uma lista de permissões (as efetivas, ou o padrão do papel). O
 * assunto sem ação chega da API com alcance `nenhuma`; aqui ele fica com o do
 * padrão do papel (`doPapel`), que é o que a API usa quando uma ação dele liga
 * sem alcance escolhido.
 */
export function estadoDa(
  lista: Lista,
  doPapel: Readonly<Record<string, Alcance>>,
): EstadoDasPermissoes {
  const ligadas = new Set<string>();
  const alcances: Record<string, Alcance> = { ...doPapel };
  for (const item of lista) {
    for (const acao of item.acoes) ligadas.add(chave(item.recurso, acao));
    if (item.alcance !== 'nenhuma') alcances[item.recurso] = item.alcance;
  }
  return { ligadas, alcances };
}

/** O assunto tem alguma ação ligada? Sem ação, o alcance dele não vale. */
export function temAcao(estado: EstadoDasPermissoes, recurso: string): boolean {
  for (const k of estado.ligadas) if (k.startsWith(`${recurso}:`)) return true;
  return false;
}

/** Uma ação do catálogo, com o assunto dela. */
export interface AcaoNaTela {
  readonly recurso: RecursoDePermissao;
  readonly acao: AcaoDePermissao;
  readonly rotulo: string;
  readonly descricao: string | null;
  readonly dependeDe: readonly { recurso: RecursoDePermissao; acao: AcaoDePermissao }[];
  readonly trava: string | null;
}

export function acoesDoCatalogo(catalogo: CatalogoDePermissoes): AcaoNaTela[] {
  return catalogo.abas.flatMap((aba) =>
    aba.assuntos.flatMap((assunto) =>
      assunto.acoes.map((acao) => ({
        recurso: assunto.recurso,
        acao: acao.acao,
        rotulo: acao.rotulo,
        descricao: acao.descricao,
        dependeDe: acao.depende_de,
        trava: acao.trava,
      })),
    ),
  );
}

/** Os assuntos com "Até onde vale": só neles o alcance se escolhe. */
export function assuntosComAlcance(catalogo: CatalogoDePermissoes): RecursoDePermissao[] {
  return catalogo.abas.flatMap((aba) =>
    aba.assuntos.filter((assunto) => assunto.tem_alcance).map((assunto) => assunto.recurso),
  );
}

/** As ações que faltam ligar para esta poder ligar. */
export function oQueFaltaLigar(
  estado: EstadoDasPermissoes,
  acao: Pick<AcaoNaTela, 'dependeDe'>,
): AcaoNaTela['dependeDe'] {
  return acao.dependeDe.filter((p) => !estado.ligadas.has(chave(p.recurso, p.acao)));
}

/**
 * Liga ou desliga uma ação. Ligar exige o que ela depende ligado (sem isso,
 * nada muda: a tela trava o toggle antes). Desligar desliga, em cadeia, o que
 * depende dela.
 */
export function alternar(
  estado: EstadoDasPermissoes,
  todas: readonly AcaoNaTela[],
  recurso: RecursoDePermissao,
  acao: AcaoDePermissao,
): EstadoDasPermissoes {
  const alvo = chave(recurso, acao);
  const ligadas = new Set(estado.ligadas);
  if (!ligadas.has(alvo)) {
    const daAcao = todas.find((a) => a.recurso === recurso && a.acao === acao);
    if (!daAcao || oQueFaltaLigar(estado, daAcao).length > 0) return estado;
    ligadas.add(alvo);
    return { ...estado, ligadas };
  }
  ligadas.delete(alvo);
  let mudou = true;
  while (mudou) {
    mudou = false;
    for (const outra of todas) {
      const k = chave(outra.recurso, outra.acao);
      if (ligadas.has(k) && outra.dependeDe.some((p) => !ligadas.has(chave(p.recurso, p.acao)))) {
        ligadas.delete(k);
        mudou = true;
      }
    }
  }
  return { ...estado, ligadas };
}

export function escolherAlcance(
  estado: EstadoDasPermissoes,
  recurso: RecursoDePermissao,
  alcance: Alcance,
): EstadoDasPermissoes {
  return { ...estado, alcances: { ...estado.alcances, [recurso]: alcance } };
}

/** "Além do papel", "Tirada do papel", ou nada. */
export function seloDa(
  estado: EstadoDasPermissoes,
  padrao: EstadoDasPermissoes,
  recurso: string,
  acao: string,
): 'alem' | 'tirada' | null {
  const k = chave(recurso, acao);
  const agora = estado.ligadas.has(k);
  if (agora === padrao.ligadas.has(k)) return null;
  return agora ? 'alem' : 'tirada';
}

/**
 * Quantas alterações há entre dois estados: cada toggle diferente, e cada
 * alcance diferente num assunto com ação ligada. Ligar a primeira ação de um
 * assunto conta uma, e não duas: o alcance dele já existia.
 */
export function quantasAlteracoes(
  estado: EstadoDasPermissoes,
  inicial: EstadoDasPermissoes,
  comAlcance: readonly string[],
): number {
  const chaves = new Set([...estado.ligadas, ...inicial.ligadas]);
  let n = 0;
  for (const k of chaves) if (estado.ligadas.has(k) !== inicial.ligadas.has(k)) n += 1;
  for (const recurso of comAlcance) {
    if (temAcao(estado, recurso) && estado.alcances[recurso] !== inicial.alcances[recurso]) n += 1;
  }
  return n;
}

/**
 * O corpo do "Salvar alterações": o que difere do padrão do papel. O travado
 * não vai (a API o recusaria), e o alcance só vai no assunto com ação ligada,
 * que é onde ele vale.
 */
export function corpoParaSalvar(
  papel: SalvarPermissoesDaPessoa['papel'],
  estado: EstadoDasPermissoes,
  padrao: EstadoDasPermissoes,
  todas: readonly AcaoNaTela[],
  comAlcance: readonly string[],
): SalvarPermissoesDaPessoa {
  const acoes: Record<string, boolean> = {};
  for (const a of todas) {
    if (a.trava !== null) continue;
    const k = chave(a.recurso, a.acao);
    const agora = estado.ligadas.has(k);
    if (agora !== padrao.ligadas.has(k)) acoes[k] = agora;
  }
  const alcances: Record<string, string> = {};
  for (const recurso of comAlcance) {
    if (!temAcao(estado, recurso)) continue;
    const agora = estado.alcances[recurso];
    if (agora !== undefined && agora !== padrao.alcances[recurso]) alcances[recurso] = agora;
  }
  return { papel, acoes, alcances };
}

/** Quem está na tela: o que ela pode (do `/v1/me`) e se só gerencia permissões. */
export interface QuemEdita {
  readonly permissoes: Me['permissoes'];
  /** Não é Administrador nem Super Admin: só dá o que tem (ADR-0034, item 5). */
  readonly limitada: boolean;
}

/** Quem edita pode ligar esta ação para alguém? Desligar, ela sempre pode. */
export function podeLigar(quem: QuemEdita, recurso: string, acao: string): boolean {
  return (
    !quem.limitada ||
    quem.permissoes.some(
      (p) => p.recurso === recurso && (p.acoes as readonly string[]).includes(acao),
    )
  );
}

/** Até onde quem edita vai num assunto: o alcance dela, já com o corte da carteira. */
function alcanceDeQuemEdita(quem: QuemEdita, recurso: string): number {
  const dela = quem.permissoes.find((p) => p.recurso === recurso)?.alcance;
  return dela === undefined || dela === 'nenhuma' ? -1 : nivel(dela);
}

/**
 * Os alcances que quem edita pode escolher num assunto com "Até onde vale".
 * Ficar até onde já estava salvo, ou descer, sempre; subir além disso, só até
 * o alcance dela, como a API confere.
 */
export function alcancesQuePodeDar(
  quem: QuemEdita,
  inicial: EstadoDasPermissoes,
  recurso: string,
): readonly Alcance[] {
  if (!quem.limitada) return NIVEIS;
  const salvo = temAcao(inicial, recurso) ? nivel(inicial.alcances[recurso]) : -1;
  const teto = Math.max(alcanceDeQuemEdita(quem, recurso), salvo);
  return NIVEIS.filter((n) => nivel(n) <= teto);
}

/**
 * `alternar`, e mais: quando o assunto ganha a primeira ação pelas mãos de
 * quem só gerencia permissões, o alcance dele desce para dentro do que ela
 * pode dar. Sem isso, a API recusaria o assunto inteiro na hora de salvar.
 */
export function alternarComo(
  quem: QuemEdita,
  inicial: EstadoDasPermissoes,
  estado: EstadoDasPermissoes,
  todas: readonly AcaoNaTela[],
  comAlcance: readonly string[],
  recurso: RecursoDePermissao,
  acao: AcaoDePermissao,
): EstadoDasPermissoes {
  const novo = alternar(estado, todas, recurso, acao);
  if (!quem.limitada || !comAlcance.includes(recurso)) return novo;
  if (temAcao(estado, recurso) || !temAcao(novo, recurso)) return novo;
  const permitidos = alcancesQuePodeDar(quem, inicial, recurso);
  const agora = novo.alcances[recurso];
  const teto = permitidos[permitidos.length - 1];
  if (teto === undefined || (agora !== undefined && permitidos.includes(agora))) return novo;
  return escolherAlcance(novo, recurso, teto);
}

/**
 * O que, de `depois`, quem edita não pode dar: as ações que ela não tem e que
 * passam a ligar, e os assuntos com "Até onde vale" cujo alcance sobe além do
 * dela. É a conta da API; a tela usa para travar "Voltar ao padrão do papel",
 * que pode religar o que outra pessoa tinha tirado.
 */
export function oQueNaoPodeDar(
  quem: QuemEdita,
  inicial: EstadoDasPermissoes,
  depois: EstadoDasPermissoes,
  comAlcance: readonly string[],
): string[] {
  if (!quem.limitada) return [];
  const fora: string[] = [];
  for (const k of depois.ligadas) {
    if (inicial.ligadas.has(k)) continue;
    const [recurso = '', acao = ''] = k.split(':');
    if (!podeLigar(quem, recurso, acao)) fora.push(k);
  }
  for (const recurso of comAlcance) {
    if (!temAcao(depois, recurso)) continue;
    const agora = nivel(depois.alcances[recurso]);
    const cresce = !temAcao(inicial, recurso) || agora > nivel(inicial.alcances[recurso]);
    if (cresce && agora > alcanceDeQuemEdita(quem, recurso)) fora.push(recurso);
  }
  return fora;
}
